import React, { Component } from 'react';
import Icon from '../../icons/Icon';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import ChartService from '../../service/ChartService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { RootState } from '../../redux/store';
import { changeChart, changeChartEditorOptions } from '../../redux/actions/ChartActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { customConfirm } from 'components/custom/CustomPopupConfirm';
import { DashboardState } from '../../redux/reducers/DashboardReducer';
import { ChartType } from '@trlogic/ui-pack';
import ChartJSComponent from './ChartJSComponent';
import { ChartJsDataModel, MetricItem, SegmentItem } from '../../domain/chart/ChartDomains';
import { Tooltip } from 'primereact/tooltip';
import { User } from '../../domain/rbac/RbacDomains';
import RoleHelper from '../../RoleHelper';
import { ChartOptions } from 'chart.js';
import JobService from '../../service/JobService';
import { ChartExportJob } from '../../domain/job/JobDomain';
import moment from 'moment';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from '../../util/LanguageUtil';

class DashboardChartContainer extends Component<Props> {
  private chartService: ChartService = ServiceContainer.getInstance().get(ServiceNames.CHART);
  private readonly jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);

  render() {
    const { chartName, data, segmentItem, metrics, chartId } = this.props;
    return (
      <div className={`flex flex-col w-full overflow-hidden h-full items-center box-border border-[1px] border-[#E0E4E8] rounded-[12px]`}>
        <div className={'flex items-center justify-between w-full h-11 shrink-0 bg-main-gray rounded-t-[12px] border-b-[1px] border-[#E0E4E8] px-5'}>
          <div className={'inline-flex items-center font-bold text-primary truncate'}>
            <h5>{chartName}</h5>
          </div>
          {!this.props.isStaticChart && (
            <div className={'flex items-center gap-x-2'}>
              <div
                onClick={(e) => this.onClickEdit(e)}
                className={`flex items-center rounded group relative w-5 h-5 bg-white border border-[#E0E4E8]  ${!RoleHelper.canEdit('Dashboard') ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon id={`chart-edit-${chartId}`} icon={'edit'} size={20} />
                <Tooltip target={`#chart-edit-${chartId}`} position='left' content={chartName} />
              </div>
              <div
                onClick={(e) => this.exportChart(e)}
                className={`flex items-center rounded group relative w-5 h-5 bg-white border border-[#E0E4E8]  ${!RoleHelper.canEdit('Dashboard') ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon id={`chart-export-${chartId}`} icon={'export'} size={20} />
                <Tooltip target={`#chart-export-${chartId}`} position='left' content={'Export chart ' + chartName} />
              </div>
              <div
                onClick={(e) => this.deleteChart(e)}
                className={`flex items-center rounded group relative w-5 h-5 bg-white border border-[#E0E4E8]  ${!RoleHelper.canEdit('Dashboard') ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon id={`chart-delete-${chartId}`} icon={'delete'} className={'text-f-black-3'} size={20} />
                <Tooltip target={`#chart-delete-${chartId}`} position='left' content={'Delete chart ' + chartName} />
              </div>
            </div>
          )}
        </div>
        {data && (
          <ChartJSComponent
            metrics={metrics}
            options={this.props.options}
            isDashboard={true}
            segmentItem={segmentItem}
            chartType={this.props.type}
            chartName={this.props.chartName}
            data={data}
          />
        )}
      </div>
    );
  }

  private onClickEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!RoleHelper.canEdit('Dashboard')) return;
    const { dashboardName, chartId, router } = this.props;
    router.navigate(`/dashboard/${dashboardName}/${chartId}`);
  };

  private exportChart = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (!RoleHelper.canEdit('Dashboard')) return;
    const { chartId, data } = this.props;
    try {
      const job: ChartExportJob = {
        type: 'CHART_EXPORT',
        timestamp: moment().valueOf(),
        status: 'PENDING',
        chartId: chartId,
        data: data as ChartJsDataModel,
        extension: 'csv',
        path: '',
      };
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.export' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.success.exportScheduled' })}</div>,
      });
    } catch (e) {
      console.error(e);
    }
  };

  private deleteChart = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (!RoleHelper.canEdit('Dashboard')) return;
    const { chartId, chartName } = this.props;
    customConfirm({
      target: event.currentTarget,
      message: `Are you sure you want to delete ${chartName}?`,
      accept: async () => {
        await this.chartService.remove(chartId);
      },
    });
  };
}

interface Props extends FormicaProps, ReduxProps {
  type: ChartType;
  chartName: string;
  chartId: string;
  data: any;
  dashboardName: string;
  isStaticChart?: boolean;
  segmentItem?: SegmentItem;
  metrics: MetricItem[];
  options: Partial<ChartOptions>;
}

const mapStateToProps = (state: RootState) => ({
  dashboard: state.dashboard as DashboardState,
  user: state.auth.user as User,
});

const mapDispatchToProps = { changeChart, changeChartEditorOptions };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(DashboardChartContainer));
